import { useState, useMemo } from 'react';
import {
  Target,
  Plus,
  Edit,
  Trash2,
  DollarSign,
  ShoppingCart,
  Receipt,
  Trophy,
  Loader2,
  ArrowRight,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  useSalesTargets,
  useSalesTarget,
  useSalesTargetProgressAll,
  useBestDayStats,
  useCreateSalesTarget,
  useUpdateSalesTarget,
  useDeleteSalesTarget,
  getProgressBarColor,
  calculateDailyTarget,
  calculateWeeklyTarget,
  type SalesTarget,
  type SalesTargetProgress,
  type PeriodType,
} from '@/hooks/useSalesTargets';
import { useLocations } from '@/hooks/useLocations';
import { useCurrency } from '@/hooks/useCurrency';
import { format } from 'date-fns';

type ActiveTab = 'configure' | 'performance';

export default function SalesTargets() {
  const { formatCurrency } = useCurrency();
  const { locations } = useLocations();

  // State
  const [activeTab, setActiveTab] = useState<ActiveTab>('configure');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    locations?.[0]?.id || null
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTarget, setEditingTarget] = useState<SalesTarget | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  // Form state
  const [targetForm, setTargetForm] = useState({
    target_sales_amount: '',
    target_order_count: '',
    target_avg_ticket: '',
  });

  // Update selected location when locations load
  useMemo(() => {
    if (!selectedLocationId && locations?.length) {
      setSelectedLocationId(locations[0].id);
    }
  }, [locations, selectedLocationId]);

  // Queries
  const { data: allTargets = [], isLoading: targetsLoading } = useSalesTargets();
  const { data: currentTarget } = useSalesTarget(selectedLocationId);
  const { data: progressAll, isLoading: progressLoading } = useSalesTargetProgressAll(selectedLocationId);
  const { data: bestDayStats } = useBestDayStats(selectedLocationId);

  // Mutations
  const createTarget = useCreateSalesTarget();
  const updateTarget = useUpdateSalesTarget();
  const deleteTarget = useDeleteSalesTarget();

  // Calculate previews
  const monthlySales = parseFloat(targetForm.target_sales_amount) || 0;
  const monthlyOrders = parseInt(targetForm.target_order_count) || 0;
  const dailySalesPreview = calculateDailyTarget(monthlySales);
  const weeklySalesPreview = calculateWeeklyTarget(monthlySales);
  const dailyOrdersPreview = Math.ceil(monthlyOrders / 26);
  const weeklyOrdersPreview = Math.ceil(monthlyOrders / 4.33);

  // Reset form
  const resetForm = () => {
    setTargetForm({
      target_sales_amount: '',
      target_order_count: '',
      target_avg_ticket: '',
    });
    setEditingTarget(null);
  };

  // Open modal for creating or editing
  const openModal = (target?: SalesTarget) => {
    if (target) {
      setEditingTarget(target);
      setTargetForm({
        target_sales_amount: target.target_sales_amount.toString(),
        target_order_count: target.target_order_count.toString(),
        target_avg_ticket: target.target_avg_ticket.toString(),
      });
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  // Save target
  const handleSave = async () => {
    if (!selectedLocationId) {
      toast.error('Error', {
        description: 'Please select a location first',
      });
      return;
    }

    try {
      const data = {
        target_sales_amount: parseFloat(targetForm.target_sales_amount) || 0,
        target_order_count: parseInt(targetForm.target_order_count) || 0,
        target_avg_ticket: parseFloat(targetForm.target_avg_ticket) || 0,
      };

      if (editingTarget) {
        await updateTarget.mutateAsync({
          id: editingTarget.id,
          ...data,
        });
        toast.success('Sales target updated successfully');
      } else {
        await createTarget.mutateAsync({
          location_id: selectedLocationId,
          ...data,
        });
        toast.success('Sales target created successfully');
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to save sales target',
      });
    }
  };

  // Delete target
  const handleDelete = async () => {
    if (!deleteTargetId || !selectedLocationId) return;

    try {
      await deleteTarget.mutateAsync({
        id: deleteTargetId,
        locationId: selectedLocationId,
      });
      toast.success('Sales target removed');
      setDeleteTargetId(null);
    } catch (error) {
      toast.error('Error', {
        description: 'Failed to remove sales target',
      });
    }
  };

  // Render progress card
  const renderProgressCard = (
    title: string,
    period: PeriodType,
    progress: SalesTargetProgress | null | undefined
  ) => {
    if (!progress) return null;

    const hasTarget = progress.target_id !== null;
    const salesPct = progress.sales_progress_pct || 0;

    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center justify-between">
            {title}
            {hasTarget && (
              <Badge variant={salesPct >= 100 ? 'default' : salesPct >= 75 ? 'secondary' : 'outline'}>
                {salesPct.toFixed(0)}%
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasTarget ? (
            <div className="space-y-3">
              {/* Progress bar */}
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all',
                    getProgressBarColor(salesPct)
                  )}
                  style={{ width: `${Math.min(salesPct, 100)}%` }}
                />
              </div>

              {/* Sales */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Sales</span>
                <span className="font-medium">
                  {formatCurrency(progress.actual_sales)} / {formatCurrency(progress.target_sales)}
                </span>
              </div>

              {/* Orders */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Orders</span>
                <span className="font-medium">
                  {progress.actual_orders} / {progress.target_orders}
                </span>
              </div>

              {/* Avg Ticket */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Avg Ticket</span>
                <span className="font-medium">
                  {formatCurrency(progress.actual_avg_ticket)}
                  {progress.target_avg_ticket > 0 && (
                    <span className="text-muted-foreground">
                      {' '}/ {formatCurrency(progress.target_avg_ticket)}
                    </span>
                  )}
                </span>
              </div>

              {/* Remaining */}
              {period !== 'daily' && progress.days_remaining > 0 && (
                <div className="pt-2 border-t text-xs text-muted-foreground">
                  {progress.days_remaining} days remaining | Need {formatCurrency(progress.sales_to_goal)} more
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No target set for this period
            </p>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Target className="h-5 w-5" />
            Sales Targets
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Set monthly goals and track your daily progress toward sales targets
          </p>
        </div>
      </div>

      {/* Location Selector */}
      {locations && locations.length > 1 && (
        <div className="flex items-center gap-2">
          <Label className="text-sm">Location:</Label>
          <Select
            value={selectedLocationId || ''}
            onValueChange={setSelectedLocationId}
          >
            <SelectTrigger className="w-[250px]">
              <SelectValue placeholder="Select location" />
            </SelectTrigger>
            <SelectContent>
              {locations.map((loc) => (
                <SelectItem key={loc.id} value={loc.id}>
                  {loc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b">
        <button
          onClick={() => setActiveTab('configure')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'configure'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Configure Targets
        </button>
        <button
          onClick={() => setActiveTab('performance')}
          className={cn(
            'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
            activeTab === 'performance'
              ? 'border-primary text-primary'
              : 'border-transparent text-muted-foreground hover:text-foreground'
          )}
        >
          Performance
        </button>
      </div>

      {/* Configure Tab */}
      {activeTab === 'configure' && (
        <div className="space-y-6">
          {/* Current Target Card */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Monthly Target</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Set your monthly goals - daily and weekly targets are calculated automatically
                </p>
              </div>
              {currentTarget ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openModal(currentTarget)}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteTargetId(currentTarget.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <Button onClick={() => openModal()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Set Target
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {currentTarget ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Monthly Sales */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <DollarSign className="h-4 w-4" />
                      Monthly Sales Goal
                    </div>
                    <p className="text-2xl font-semibold">
                      {formatCurrency(currentTarget.target_sales_amount)}
                    </p>
                    <div className="text-xs text-muted-foreground">
                      Daily: {formatCurrency(calculateDailyTarget(currentTarget.target_sales_amount))} |
                      Weekly: {formatCurrency(calculateWeeklyTarget(currentTarget.target_sales_amount))}
                    </div>
                  </div>

                  {/* Monthly Orders */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <ShoppingCart className="h-4 w-4" />
                      Monthly Order Goal
                    </div>
                    <p className="text-2xl font-semibold">
                      {currentTarget.target_order_count.toLocaleString()}
                    </p>
                    <div className="text-xs text-muted-foreground">
                      Daily: ~{Math.ceil(currentTarget.target_order_count / 26)} |
                      Weekly: ~{Math.ceil(currentTarget.target_order_count / 4.33)}
                    </div>
                  </div>

                  {/* Target Avg Ticket */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Receipt className="h-4 w-4" />
                      Target Avg Ticket
                    </div>
                    <p className="text-2xl font-semibold">
                      {formatCurrency(currentTarget.target_avg_ticket)}
                    </p>
                    <div className="text-xs text-muted-foreground">
                      Effective from {format(new Date(currentTarget.effective_from), 'MMM d, yyyy')}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No target set for this location</p>
                  <p className="text-sm mt-1">
                    Set a monthly sales target to start tracking your progress
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* All Locations Summary (if multiple locations) */}
          {locations && locations.length > 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">All Locations</CardTitle>
              </CardHeader>
              <CardContent>
                {targetsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Location</TableHead>
                        <TableHead className="text-right">Monthly Sales</TableHead>
                        <TableHead className="text-right">Monthly Orders</TableHead>
                        <TableHead className="text-right">Avg Ticket</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {locations.map((loc) => {
                        const target = allTargets.find((t) => t.location_id === loc.id);
                        return (
                          <TableRow key={loc.id}>
                            <TableCell className="font-medium">{loc.name}</TableCell>
                            <TableCell className="text-right">
                              {target ? formatCurrency(target.target_sales_amount) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {target ? target.target_order_count.toLocaleString() : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              {target ? formatCurrency(target.target_avg_ticket) : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedLocationId(loc.id);
                                  if (target) {
                                    openModal(target);
                                  } else {
                                    openModal();
                                  }
                                }}
                              >
                                {target ? 'Edit' : 'Set'}
                                <ChevronRight className="h-4 w-4 ml-1" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-6">
          {progressLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : (
            <>
              {/* Progress Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {renderProgressCard('Daily Progress', 'daily', progressAll?.daily)}
                {renderProgressCard('Weekly Progress', 'weekly', progressAll?.weekly)}
                {renderProgressCard('Monthly Progress', 'monthly', progressAll?.monthly)}
              </div>

              {/* Best Day Stats */}
              {bestDayStats && (bestDayStats.best_sales_amount || bestDayStats.best_orders_count) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Trophy className="h-5 w-5 text-amber-500" />
                      Best Day This Year
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {bestDayStats.best_sales_amount && (
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-green-100 rounded-lg">
                            <DollarSign className="h-6 w-6 text-green-600" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Highest Sales Day</p>
                            <p className="text-xl font-semibold">
                              {formatCurrency(bestDayStats.best_sales_amount)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {bestDayStats.best_sales_date &&
                                format(new Date(bestDayStats.best_sales_date), 'EEEE, MMMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                      )}

                      {bestDayStats.best_orders_count && (
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-blue-100 rounded-lg">
                            <ShoppingCart className="h-6 w-6 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Most Orders Day</p>
                            <p className="text-xl font-semibold">
                              {bestDayStats.best_orders_count} orders
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {bestDayStats.best_orders_date &&
                                format(new Date(bestDayStats.best_orders_date), 'EEEE, MMMM d, yyyy')}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Empty State */}
              {!progressAll?.daily?.target_id && !progressAll?.monthly?.target_id && (
                <Card>
                  <CardContent className="py-12 text-center">
                    <Target className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-lg font-medium mb-2">No targets set</h3>
                    <p className="text-muted-foreground mb-4">
                      Set a monthly sales target to start tracking your performance
                    </p>
                    <Button onClick={() => setActiveTab('configure')}>
                      Configure Targets
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <ResponsiveModal open={modalOpen} onOpenChange={setModalOpen}>
        <ResponsiveModalContent>
          <ResponsiveModalHeader>
            <ResponsiveModalTitle>
              {editingTarget ? 'Edit Sales Target' : 'Set Sales Target'}
            </ResponsiveModalTitle>
            <ResponsiveModalDescription>
              Set your monthly goals. Daily and weekly targets are calculated automatically.
            </ResponsiveModalDescription>
          </ResponsiveModalHeader>

          <ResponsiveModalBody>
            <div className="space-y-4">
              {/* Monthly Sales */}
              <div className="space-y-2">
                <Label htmlFor="target_sales_amount">Monthly Sales Target</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="target_sales_amount"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="pl-9"
                    value={targetForm.target_sales_amount}
                    onChange={(e) =>
                      setTargetForm({ ...targetForm, target_sales_amount: e.target.value })
                    }
                  />
                </div>
                {monthlySales > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Daily: ~{formatCurrency(dailySalesPreview)} | Weekly: ~{formatCurrency(weeklySalesPreview)}
                  </p>
                )}
              </div>

              {/* Monthly Orders */}
              <div className="space-y-2">
                <Label htmlFor="target_order_count">Monthly Order Target</Label>
                <div className="relative">
                  <ShoppingCart className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="target_order_count"
                    type="number"
                    min="0"
                    placeholder="0"
                    className="pl-9"
                    value={targetForm.target_order_count}
                    onChange={(e) =>
                      setTargetForm({ ...targetForm, target_order_count: e.target.value })
                    }
                  />
                </div>
                {monthlyOrders > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Daily: ~{dailyOrdersPreview} orders | Weekly: ~{weeklyOrdersPreview} orders
                  </p>
                )}
              </div>

              {/* Target Avg Ticket */}
              <div className="space-y-2">
                <Label htmlFor="target_avg_ticket">Target Average Ticket (optional)</Label>
                <div className="relative">
                  <Receipt className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="target_avg_ticket"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    className="pl-9"
                    value={targetForm.target_avg_ticket}
                    onChange={(e) =>
                      setTargetForm({ ...targetForm, target_avg_ticket: e.target.value })
                    }
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Leave at 0 to auto-calculate from sales/orders
                </p>
              </div>
            </div>
          </ResponsiveModalBody>

          <ResponsiveModalFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={createTarget.isPending || updateTarget.isPending}
            >
              {(createTarget.isPending || updateTarget.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingTarget ? 'Update Target' : 'Set Target'}
            </Button>
          </ResponsiveModalFooter>
        </ResponsiveModalContent>
      </ResponsiveModal>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTargetId} onOpenChange={() => setDeleteTargetId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Sales Target?</AlertDialogTitle>
            <AlertDialogDescription>
              This will archive the current sales target. Historical data will be preserved.
              You can set a new target at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove Target
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
